import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { qualityPoints, images } from "../../data/content";
import OrbitPattern from "../OrbitPattern";

const ease = [0.22, 1, 0.36, 1];

const Quality = () => {
  const { isAr, t } = useLang();
  const framework = isAr ? qualityPoints.ar_left : qualityPoints.en_right;
  const safety = isAr ? qualityPoints.ar_safety : qualityPoints.en_safety;
  const pillars = [
    { value: "05", ar: "مراحل جودة", en: "quality gates" },
    { value: "05", ar: "معايير سلامة", en: "safety checks" },
    { value: "QA", ar: "اعتماد تشغيلي", en: "operational approval" },
  ];

  return (
    <section
      className="relative bg-brand-green-deep text-bg-ivory py-[120px] md:py-[180px] overflow-hidden"
      data-testid="quality-section"
    >
      <div className="absolute -bottom-[400px] -start-[300px]">
        <OrbitPattern size={1200} opacity={0.06} />
      </div>
      <div className="absolute inset-0 opacity-[0.1]" style={{ background: "linear-gradient(135deg, rgba(232,211,195,0.08) 0 1px, transparent 1px 100%)", backgroundSize: "48px 48px" }} />
      
      <div className="relative z-10 max-w-container mx-auto px-6 md:px-10 lg:px-14">
        {/* Header */}
        <div className="flex justify-between items-end border-b border-brand-champagne/20 pb-6 mb-16 md:mb-24" dir={isAr ? "rtl" : "ltr"}>
          <motion.span 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-mono text-sm tracking-widestest uppercase text-brand-champagne/80"
          >
            [ {t("الجودة والسلامة", "Quality & Safety")} ]
          </motion.span>
          <motion.span 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-mono text-sm text-brand-champagne/50"
          >
            20 — SEC
          </motion.span>
        </div>

        <div className="grid grid-cols-12 gap-x-8 gap-y-16 lg:gap-x-16 items-stretch">
          
          {/* Left Column (Text & Stats) */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
            className={`col-span-12 lg:col-span-5 flex flex-col justify-between ${isAr ? "lg:order-2 text-right" : "lg:order-1 text-left"}`}
            dir={isAr ? "rtl" : "ltr"}
          >
            <div>
              <h3
                className={`${isAr ? "font-arabic font-medium" : "font-serif"} text-bg-ivory max-w-[800px] leading-[1.05] tracking-tight`}
                style={{ fontSize: isAr ? "clamp(42px, 5vw, 76px)" : "clamp(52px, 6vw, 96px)" }}
              >
                {isAr ? "الجودة والامتثال والسلامة الصناعية" : (<>Quality, Compliance<br /><em className="font-light text-brand-champagne/90">& Industrial Safety</em></>)}
              </h3>
              <p
                className={`mt-8 max-w-[520px] text-brand-champagne/70 ${isAr ? "font-arabic" : "font-sans"} leading-relaxed`}
                style={{ fontSize: isAr ? 17 : 16 }}
              >
                {t(
                  "نطبّق رقابة متعددة المراحل ومعايير سلامة تشغيلية واضحة لضمان ثبات الجودة من المواد الخام حتى التغليف والتسليم. نضمن في كل خطوة أداءً صناعياً موثوقاً يفوق التوقعات.",
                  "We apply multi-stage quality control and clear safety standards from raw materials through packaging and delivery. Ensuring reliable industrial performance that exceeds expectations at every step."
                )}
              </p>
            </div>

            {/* Stats block */}
            <div className="mt-16 lg:mt-32">
              <div className="grid grid-cols-3 gap-6">
                {pillars.map((pillar, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: i * 0.15, ease }}
                    className="relative group pt-6 border-t border-brand-champagne/20"
                  >
                    {/* Hover line effect */}
                    <div className={`absolute top-0 ${isAr ? "right-0" : "left-0"} h-[1px] w-0 bg-brand-champagne transition-all duration-700 ease-out group-hover:w-full`} />
                    
                    <div className={`font-serif text-[42px] lg:text-[56px] leading-none text-brand-champagne mb-3 font-light tracking-tight transition-transform duration-500 ease-out ${isAr ? "group-hover:-translate-x-2" : "group-hover:translate-x-2"}`}>
                      {pillar.value}
                    </div>
                    <div className={`text-sm tracking-wider uppercase text-bg-ivory/60 ${isAr ? "font-arabic" : "font-sans"}`}>
                      {isAr ? pillar.ar : pillar.en}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right Column (Image & Lists) */}
          <div className={`col-span-12 lg:col-span-7 ${isAr ? "lg:order-1" : "lg:order-2"}`}>
            <div className="relative h-full min-h-[700px] group overflow-hidden bg-brand-green-dark rounded-sm">
              {/* Image with subtle zoom on hover */}
              <motion.img
                initial={{ scale: 1.1, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease }}
                src={images.qualityCheck}
                alt={t("رقابة الجودة", "Quality Inspection")}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105"
                style={{ filter: "saturate(0.6) contrast(1.1) brightness(0.85)" }}
              />
              
              {/* Premium Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-green-deep via-brand-green-deep/40 to-transparent mix-blend-multiply opacity-90" />
              
              {/* Lists Container - Glassmorphic floating panels */}
              <div className="absolute bottom-0 inset-x-0 p-6 md:p-10 flex flex-col md:flex-row gap-6" dir={isAr ? "rtl" : "ltr"}>
                {[
                  { title: t("منظومة الجودة", "QUALITY FRAMEWORK"), items: framework },
                  { title: t("معايير السلامة", "SAFETY STANDARDS"), items: safety },
                ].map((group, groupIndex) => (
                  <motion.div 
                    key={group.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.3 + (groupIndex * 0.2), ease }}
                    className="flex-1 border border-brand-champagne/10 bg-brand-green-deep/60 backdrop-blur-xl p-8 hover:bg-brand-green-deep/80 transition-colors duration-500 rounded-sm"
                  >
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-champagne/80" />
                      <div className={`font-mono text-xs tracking-widest uppercase text-brand-champagne/90 ${isAr ? "font-arabic" : ""}`}>
                        {group.title}
                      </div>
                    </div>
                    
                    <ul className="space-y-4">
                      {group.items.map((q, i) => (
                        <li key={q} className="group/item flex items-start gap-4">
                          <span className="font-mono text-xs text-brand-champagne/40 mt-1 transition-colors duration-300 group-hover/item:text-brand-champagne/80">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className={`text-bg-ivory/80 text-[15px] leading-relaxed transition-colors duration-300 group-hover/item:text-bg-ivory ${isAr ? "font-arabic" : "font-sans"}`}>
                            {q}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>

              {/* Decorative Large Text */}
              <div
                className={`absolute top-10 ${isAr ? "left-10" : "right-10"} font-serif italic font-light text-brand-champagne/20 text-5xl md:text-7xl select-none pointer-events-none mix-blend-overlay`}
              >
                {t("التميز الصناعي", "Zero Defect")}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Quality;
